import { useEffect, useState } from 'react';
import { isNil } from 'lodash-es';
import { useQueryClient } from 'react-query';

import { RosterClient, SlackTargetingClient, SlackUser } from 'jf/api';
import { useClientMutation, useClientQuery } from 'jf/utils/useClientQuery';

const listener = (event) => {
  // Cancel the event as stated by the standard.
  event.preventDefault();
  // Chrome requires returnValue to be set.
  event.returnValue = '';
};
export const useStudySlackTargetUpdater = (studyRef: string | undefined) => {
  const queryClient = useQueryClient();

  const { data: slackTargets } = useClientQuery(SlackTargetingClient.getStudySlackTargets, {
    studyRef,
  });

  const { mutateAsync: updateStudyRosterSlackTargets } = useClientMutation(
    RosterClient.updateStudyRosterSlackTargets
  );
  const { mutateAsync: deleteSlackTargets } = useClientMutation(
    SlackTargetingClient.deleteStudySlackTargets
  );

  const [isUpdating, setIsUpdating] = useState(false);

  useEffect(() => {
    // if we're updating, we should prevent user from refreshing the page
    // this would make it so some queries will not get a chance to fire
    // and reload will remove users that looked like they were set.
    if (isUpdating) {
      window.addEventListener('beforeunload', listener);
    } else {
      window.removeEventListener('beforeunload', listener);
    }
  }, [isUpdating]);

  const update = async (newSlackTargets: SlackUser[]) => {
    if (isNil(slackTargets)) {
      return;
    }

    const queries: Promise<any>[] = [];

    // create new targets
    const slackTargetsToCreate = newSlackTargets.filter((target) => {
      const targetAlreadyExists = slackTargets.find(
        (oldTarget) => oldTarget.id === target.slackUserId
      );
      return !targetAlreadyExists;
    });
    for (const slackTarget of slackTargetsToCreate) {
      queries.push(
        // This is where we should save each new slack user to be targeted
        updateStudyRosterSlackTargets({
          studyRef: studyRef!,
          slackUserRef: slackTarget.publicReferenceId,
        })
      );
    }

    // delete removed targets
    const slackTargetsToDelete = slackTargets.filter((target) => {
      const targetStillExists = newSlackTargets.find(
        (newtarget) => target.id === newtarget.slackUserId
      );
      return !targetStillExists;
    });

    if (slackTargetsToDelete.length) {
      queries.push(
        deleteSlackTargets({
          studyRef: studyRef!,
          slackTargetId: slackTargetsToDelete.map((target) => target.id),
        })
      );
    }
    if (queries.length) {
      setIsUpdating(true);
      return Promise.allSettled(queries).then(() => {
        setIsUpdating(false);
        queryClient.refetchQueries(['GET_STUDY_SLACK_TARGETS', studyRef]);
      });
    }
  };

  return { update, isUpdating };
};
